import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { styles } from '../styles/components/TermsAgreementForm.styles';
import { useRegistrationFlow } from '../context/RegistrationFlowContext';
import { ROUTES } from '../config/routes';
import { USAGE_NOTES_TEXT, TERMS_SUBTITLE_TEXT, MEMBER_TERMS_TEXT, generateTermsHTML } from '../data/textsData';

const TermsAgreementForm = ({ onTermsComplete, language = 'ja' }) => {
  const navigate = useNavigate();
  const { completeStep, REGISTRATION_STEPS } = useRegistrationFlow();

  // 2つのスクロール状態を管理
  const [hasScrolledUsageNotes, setHasScrolledUsageNotes] = useState(false);

  // 同意チェックボックスの状態
  const [agreed, setAgreed] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);

  const usageNotesContainerRef = useRef(null);
  const agreementContainerRef = useRef(null);

  // 利用時の注意点のスクロールハンドラー
  const handleUsageNotesScroll = (e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setHasScrolledUsageNotes(true);
    }
  };

  // 2つ全てがスクロール完了しているか確認
  const allScrolled = hasScrolledUsageNotes;

  // すべての条件を満たしているか（スクロール完了 + 同意チェック）
  const canSubmit = allScrolled && agreed;

  const handleSubmit = () => {
    if (!allScrolled) {
      alert('すべての規約を最後までスクロールしてご確認ください。');
      return;
    }

    if (!agreed) {
      alert('利用規約に同意してください。');
      return;
    }

    setIsProcessing(true);

    try {
      const agreementData = {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        agreed: true,
        language: language,
        usageNotesRead: hasScrolledUsageNotes
      };

      // 登録フローの状態を更新
      completeStep(REGISTRATION_STEPS.TERMS_AGREEMENT, { termsAgreed: true });

      // onTermsCompleteコールバックが提供されている場合は実行
      if (onTermsComplete) {
        onTermsComplete(agreementData);
      }

      // 次画面(認証コードフォーム)に遷移
      navigate(ROUTES.AUTH_CODE, { replace: true });

    } catch (error) {
      console.error('エラーが発生しました:', error);
      alert('保存に失敗しました。');
    } finally {
      setIsProcessing(false);
    }
  };

  // 別タブで会員規約を開く
  const openTermsInNewTab = () => {
    const termsWindow = window.open('', '_blank');
    termsWindow.document.write(generateTermsHTML(MEMBER_TERMS_TEXT));
    termsWindow.document.close();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>利用規約と合意事項</h2>
        <p style={styles.subtitle}>
          <span>ご利用前に必ず以下の内容をすべてお読みいただき、ご確認ください。</span><br />
          <span>卓トレ会員規約は、会員登録後もマイページからいつでも閲覧・確認いただけます。</span>
        </p>
      </div>

      <div style={styles.notice}>
        <strong> 2つの規約を必ず最後までご確認ください</strong>
      </div>

      {/* 会員規約セクション */}
      <div style={styles.documentSection}>
        <div style={styles.documentHeader}>
          <h3 style={styles.documentTitle}>
            <span>利用時の注意点と合意事項（抜粋）</span>
            {hasScrolledUsageNotes && <span style={styles.checkMark}> ✓</span>}
          </h3>
        </div>

        <div
          ref={usageNotesContainerRef}
          onScroll={handleUsageNotesScroll}
          style={styles.scrollContainer}
        >
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '14px' }}>
            {USAGE_NOTES_TEXT}
          </div>
        </div>

        <div style={styles.scrollStatus}>
          {hasScrolledUsageNotes ? (
            <span style={styles.scrollComplete}>✓ 利用時の注意点を最後まで確認しました</span>
          ) : (
            <span style={styles.scrollIncomplete}>⬇ 利用時の注意点を最後までスクロールしてください</span>
          )}
        </div>
      </div>

      {/* 利用合意事項セクション */}
      <div style={styles.documentSection}>
        <div style={{
          ...styles.documentHeader,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '15px'
        }}>
          <div style={{ flex: 1 }}>
            <h3 style={styles.documentTitle}>
              <span>卓トレ会員規約</span>
              <br />
              <span style={styles.termsSubtitle}>
                {TERMS_SUBTITLE_TEXT}
              </span>
            </h3>
          </div>
          <button
            onClick={openTermsInNewTab}
            style={styles.openTabButton}
            title="別タブで規約を開きます"
          >
            全画面で開く
          </button>
        </div>

        <div
          ref={agreementContainerRef}
          style={styles.scrollContainer}
        >
          <div
            style={{
              whiteSpace: 'pre-wrap',
              lineHeight: '1.8',
              fontSize: '14px',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%'
            }}
            dangerouslySetInnerHTML={{
              __html: MEMBER_TERMS_TEXT
                .replace(/<strike>/g, '<span style="text-decoration: line-through;">')
                .replace(/<\/strike>/g, '</span>')
                .replace(/<campaign>/g, '<span style="display: inline-block; border: 1.5px solid #e53935; color: #e53935; padding: 2px 8px; margin-left: 12px; font-size: 11px; font-weight: bold; border-radius: 3px;">')
                .replace(/<\/campaign>/g, '</span>')
            }}
          />
        </div>
      </div>

      {/* 同意チェックボックス */}
      <div style={styles.agreementCheckboxContainer}>
        <label style={{
          ...styles.agreementCheckboxLabel,
          ...(allScrolled ? styles.agreementCheckboxLabelEnabled : styles.agreementCheckboxLabelDisabled)
        }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={!allScrolled}
            style={{
              ...styles.agreementCheckbox,
              ...(allScrolled ? styles.agreementCheckboxEnabled : styles.agreementCheckboxDisabled)
            }}
          />
          <span>
            上記の利用時の注意点と合意事項（抜粋）と卓トレ会員規約の内容をすべて確認しました
          </span>
        </label>
        {!allScrolled && (
          <div style={styles.agreementCheckboxHint}>
            ※ すべての規約を最後まで確認してからチェックしてください
          </div>
        )}
      </div>

      {/* 同意ボタン */}
      <div style={styles.buttonContainer}>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || isProcessing}
          style={{
            ...styles.button,
            ...((!canSubmit || isProcessing) && styles.buttonDisabled)
          }}
        >
          {isProcessing ? '処理中...' : '次へ'}
        </button>
      </div>
    </div>
  );
};

export default TermsAgreementForm;